import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Country } from 'country-state-city';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { AllRegulationsTable } from '../../dashboard/AllRegulationsTable';
import { useDashboardData } from '../../dashboard/hooks/useDashboardData';
import type { DateFilterType, TimeRangeType } from '../../dashboard/hooks/useDashboardData';
import { apiRequest } from '../../../api/client';
import dashboardStyles from '../../dashboard/DashboardPage.module.css';
import styles from './EnterpriseContentPage.module.css';
import { BRAND_ORANGE_DEEP } from '../../../design-system/chartColors';

export default function EnterpriseRegulationsPage({ adminMode = false }: { adminMode?: boolean } = {}) {
  const { token } = useAuth();
  const { theme } = useTheme();
  const [selectedJurisdiction, setSelectedJurisdiction] = useState('All');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRangeType>('All');
  const [showHidden, setShowHidden] = useState(false);
  const [selectedEnforcement, setSelectedEnforcement] = useState<'' | 'enforced' | 'pending' | 'inactive'>('');
  // Time Range filters by when a regulation was ADDED to Policyora (discovery
  // date), so "Last 7 days" surfaces recently-ingested regulations regardless of
  // their (often older) legislative action date.
  const [dateFilterType] = useState<DateFilterType>('date_added');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [allCountryOptions, setAllCountryOptions] = useState<{ id: string; name: string; iso2: string }[]>([]);

  const getDateRangeForAPI = (): { start_date?: string; end_date?: string; days?: number } => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    if (selectedTimeRange === 'Custom') {
      return { start_date: customStartDate || undefined, end_date: customEndDate || undefined };
    }
    if (selectedTimeRange === 'All') return {};
    const daysMap: Record<string, number> = { '24H': 1, '7D': 7, '30D': 30, '90D': 90, '1Y': 365 };
    const days = daysMap[selectedTimeRange] || 7;
    const start = new Date(now);
    start.setDate(start.getDate() - days);
    return { start_date: start.toISOString().split('T')[0], end_date: today };
  };

  const {
    jurisdictions,
    countryStats,
    isLoadingJurisdictions,
    isLoadingRegulations,
    supranationalEntries,
  } = useDashboardData(token, {
    selectedJurisdiction,
    selectedTimeRange,
    dateFilterType,
    customStartDate,
    customEndDate,
    selectedCountry,
    selectedContinent: 'All',
    activeFeedTab: 'all',
    getDateRangeForAPI,
  });

  useEffect(() => {
    apiRequest<{ id: string; name: string; iso2: string }[]>('/api/v1/dashboard/countries')
      .then(setAllCountryOptions)
      .catch(() => {});
  }, []);

  // SRS #16: arriving from the map's "jump" (?iso2=US or ?jurisdiction=European Union) pre-selects the filter.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const iso2 = searchParams.get('iso2');
    const name = searchParams.get('jurisdiction');
    if (iso2 && allCountryOptions.length) {
      const c = allCountryOptions.find(x => x.iso2?.toUpperCase() === iso2.toUpperCase());
      if (c) { setSelectedCountry(c.id); setSelectedTimeRange('All'); return; }
    }
    if (name && Array.isArray(jurisdictions) && jurisdictions.length) {
      const j = jurisdictions.find(x => x.name === name || (x.code || '').toUpperCase() === (iso2 || '').toUpperCase());
      if (j) { setSelectedJurisdiction(j.id); setSelectedTimeRange('All'); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, allCountryOptions, jurisdictions]);

  const handleTimeRangeChange = (value: TimeRangeType) => {
    setSelectedTimeRange(value);
  };

  const hasActiveFilters = selectedJurisdiction !== 'All' || !!selectedCountry || selectedTimeRange !== 'All' || !!selectedEnforcement;

  const resetFilters = () => {
    setSelectedJurisdiction('All');
    setSelectedCountry('');
    setSelectedTimeRange('All');
    setSelectedEnforcement('');
    setCustomStartDate('');
    setCustomEndDate('');
  };

  const filteredCountryOptions = useMemo(() => {
    if (!Object.keys(countryStats).length || !allCountryOptions.length) return allCountryOptions;
    return allCountryOptions.filter(c => {
      const iso = c.iso2?.toUpperCase();
      return iso && countryStats[iso] !== undefined && !!Country.getCountryByCode(iso);
    });
  }, [countryStats, allCountryOptions]);

  const filteredJurisdictions = useMemo(() => {
    if (!Array.isArray(jurisdictions)) return [];

    if (selectedCountry?.startsWith('__supra__')) {
      const prefix = selectedCountry.replace('__supra__', '').toUpperCase();
      return jurisdictions.filter(j => {
        const code = (j.code || '').toUpperCase();
        return code.startsWith(`${prefix}-`) || code === prefix;
      });
    }

    return jurisdictions;
  }, [jurisdictions, selectedCountry]);

  return (
    <div className={styles.page}>
      <div className={dashboardStyles.inlineFilterBar}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 }}>
          <label style={{ fontWeight: 500, fontSize: '0.95rem', marginBottom: 4, display: 'block' }}>
            Country
          </label>
          <select
            value={selectedCountry}
            onChange={e => setSelectedCountry(e.target.value)}
            className={dashboardStyles.inlineFilterSelect}
          >
            <option value="">All Countries</option>
            {filteredCountryOptions.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
            {supranationalEntries.length > 0 && (
              <optgroup label="Supranational Bodies">
                {supranationalEntries.map(s => (
                  <option key={s.code} value={`__supra__${s.code}`}>{s.name}</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 }}>
          <label
            style={{ fontWeight: 500, fontSize: '0.95rem', marginBottom: 4, display: 'block' }}
            htmlFor="enterprise-jurisdiction-select"
          >
            Jurisdiction
          </label>
          <select
            id="enterprise-jurisdiction-select"
            value={selectedJurisdiction}
            onChange={e => setSelectedJurisdiction(e.target.value)}
            disabled={isLoadingJurisdictions}
            className={dashboardStyles.inlineFilterSelect}
          >
            <option value="All">All Jurisdictions</option>
            {filteredJurisdictions.map(j => (
              <option key={j.id} value={j.id}>{j.name}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 }}>
          <label
            style={{ fontWeight: 500, fontSize: '0.95rem', marginBottom: 4, display: 'block' }}
            htmlFor="enterprise-time-range-select"
          >
            Time Range
          </label>
          <select
            id="enterprise-time-range-select"
            value={selectedTimeRange}
            onChange={e => handleTimeRangeChange(e.target.value as TimeRangeType)}
            className={dashboardStyles.inlineFilterSelect}
          >
            <option value="All">All Time</option>
            <option value="24H">Last 24 Hours</option>
            <option value="7D">Last 7 Days</option>
            <option value="30D">Last 30 Days</option>
            <option value="90D">Last 90 Days</option>
            <option value="1Y">Last Year</option>
            <option value="Custom">Custom Range</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 }}>
          <label
            style={{ fontWeight: 500, fontSize: '0.95rem', marginBottom: 4, display: 'block' }}
            htmlFor="enterprise-enforcement-select"
          >
            Status
          </label>
          <select
            id="enterprise-enforcement-select"
            value={selectedEnforcement}
            onChange={e => setSelectedEnforcement(e.target.value as '' | 'enforced' | 'pending' | 'inactive')}
            className={dashboardStyles.inlineFilterSelect}
          >
            <option value="">All Statuses</option>
            <option value="enforced">Enforced / In force</option>
            <option value="pending">Pending / In progress</option>
            <option value="inactive">Inactive / Dead</option>
          </select>
        </div>

        {selectedTimeRange === 'Custom' && (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <input
              type="date"
              value={customStartDate}
              onChange={e => setCustomStartDate(e.target.value)}
              style={{
                padding: '7px 10px',
                borderRadius: 7,
                border: `1px solid ${theme === 'dark' ? '#344054' : '#eaecf0'}`,
                background: theme === 'dark' ? '#1d2939' : 'var(--ds-surface-2)',
                color: theme === 'dark' ? '#eaecf0' : '#101828',
                fontSize: 13,
              }}
            />
            <span style={{ alignSelf: 'center', color: 'var(--ds-text-muted)', fontSize: 12 }}>to</span>
            <input
              type="date"
              value={customEndDate}
              onChange={e => setCustomEndDate(e.target.value)}
              style={{
                padding: '7px 10px',
                borderRadius: 7,
                border: `1px solid ${theme === 'dark' ? '#344054' : '#eaecf0'}`,
                background: theme === 'dark' ? '#1d2939' : 'var(--ds-surface-2)',
                color: theme === 'dark' ? '#eaecf0' : '#101828',
                fontSize: 13,
              }}
            />
          </div>
        )}

        {hasActiveFilters && (
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={resetFilters}
              style={{
                padding: '7px 14px',
                borderRadius: 7,
                border: `1px solid ${theme === 'dark' ? '#344054' : '#eaecf0'}`,
                background: 'transparent',
                color: theme === 'dark' ? '#98a2b3' : '#667085',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Clear All
            </button>
          </div>
        )}

        {adminMode && (
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => setShowHidden(h => !h)}
              title="Admin: toggle between live and hidden (soft-deleted) records"
              style={{
                padding: '7px 14px',
                borderRadius: 7,
                border: `1px solid ${showHidden ? '#d92d20' : (theme === 'dark' ? '#344054' : '#eaecf0')}`,
                background: showHidden ? 'rgba(217,45,32,0.08)' : 'transparent',
                color: showHidden ? '#d92d20' : (theme === 'dark' ? '#98a2b3' : '#667085'),
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {showHidden ? 'Viewing hidden — show live' : 'Show hidden records'}
            </button>
          </div>
        )}
      </div>

      {adminMode && (
        <div
          style={{
            margin: '0 0 12px',
            padding: '10px 14px',
            borderRadius: 8,
            background: theme === 'dark' ? 'rgba(31,94,255,0.10)' : '#eff4ff',
            border: `1px solid ${theme === 'dark' ? '#344054' : '#d1e0ff'}`,
            color: theme === 'dark' ? '#98a2b3' : BRAND_ORANGE_DEEP,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <strong>Data cleanup:</strong> each row below has a <strong>Hide</strong> action (reversible — removes it from listings) and a <strong>Delete</strong> action (permanent — purges the record from the database, search &amp; RAG). Use <em>“Show hidden records”</em> above to review or restore hidden items.
        </div>
      )}

      <AllRegulationsTable
        jurisdictions={jurisdictions}
        jurisdictionId={
          selectedCountry?.startsWith('__supra__')
            ? selectedCountry.replace('__supra__', '')
            : selectedJurisdiction !== 'All' ? selectedJurisdiction : undefined
        }
        countryId={selectedCountry && !selectedCountry.startsWith('__supra__') ? selectedCountry : undefined}
        isLoading={isLoadingRegulations}
        selectedTimeRange={selectedTimeRange}
        dateFilterType={dateFilterType}
        customStartDate={customStartDate}
        customEndDate={customEndDate}
        enforcement={selectedEnforcement || undefined}
        adminMode={adminMode}
        showHidden={adminMode && showHidden}
      />
    </div>
  );
}
